<script setup lang="ts">
  import { computed } from 'vue';
  import Arrow from '@/shared/assets/icons/Arrow.svg';
  import { IconImage } from '@/shared/ui';
  import { usePaginationEntities, type EntityType } from '@/shared/lib/use/usePaginationEntities';

  const props = defineProps({
    totalItems: {
      type: Number,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    entityName: {
      type: String as () => EntityType,
      required: false,
      default: 'documento' as EntityType,
    },
    hideSummary: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['page-changed']);

  const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage));

  const startItem = computed(() => (props.currentPage - 1) * props.itemsPerPage + 1);
  const endItem = computed(() =>
    Math.min(props.currentPage * props.itemsPerPage, props.totalItems)
  );

  const MAX_VISIBLE_PAGES = 1;

  // Função para calcular as páginas visíveis
  // o sistema sempre exibirá a primeira e a última página e colocará "..." entre elas dependendo do número de MAX_VISIBLE_PAGES
  // Ex: [1, ..., 3, 4, 5, 6, 7, ..., 10] para MAX_VISIBLE_PAGES = 5
  const visiblePages = computed(() => {
    const range = [];
    let start = Math.max(1, props.currentPage - Math.floor(MAX_VISIBLE_PAGES / 2));
    const end = Math.min(start + MAX_VISIBLE_PAGES - 1, totalPages.value);

    // Ajusta o início se estiver próximo ao final
    if (end === totalPages.value) {
      start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);
    }

    // Adiciona reticências no início se necessário
    if (start > 1) {
      range.push(1);
      if (start > 2) {
        range.push('...');
      }
    }

    // Adiciona as páginas do meio
    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    // Adiciona reticências no final se necessário
    if (end < totalPages.value) {
      if (end < totalPages.value - 1) {
        range.push('...');
      }
      range.push(totalPages.value);
    }

    return range;
  });

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages.value) {
      emit('page-changed', page);
    }
  };

  const { getEntityConfig } = usePaginationEntities();
  const getEntityPlural = computed(() => getEntityConfig(props.entityName).plural);
  const getNoneFoundMessage = computed(() => getEntityConfig(props.entityName).noneFoundMessage);

  const pageNumberInactiveClass = 'bg-white-01 border-gray-02 text-gray-500 hover:bg-gray-50';

  const pageNumberActiveClass = 'bg-[var(--theme-secondary)] text-white';

  const ellipsisTextClass = 'text-gray-500';
</script>

<template>
  <div class="flex flex-col">
    <div
      v-if="!hideSummary"
      class="flex justify-start py-3"
    >
      <p
        v-if="totalItems === 0"
        class="text-md pt-2 pl-8 text-gray-03"
        aria-live="polite"
      >
        {{ getNoneFoundMessage }}
      </p>
      <p
        v-else
        class="text-md pt-2 pl-8 text-gray-03"
        aria-live="polite"
      >
        Mostrando <span>{{ startItem }}</span>-<span>{{ endItem }}</span> de <span>{{ totalItems }}</span> {{ getEntityPlural }}
      </p>
    </div>
    <div class="flex justify-center py-6">
      <nav
        class="flex items-center gap-2"
        role="navigation"
        :aria-label="`Navegação de páginas para ${getEntityPlural}`"
      >
        <button
          :disabled="currentPage === 1"
          :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }"
          class="flex items-center px-4 py-2 border border-gray-02 rounded-lg bg-white-01 text-sm font-medium text-gray-07 hover:bg-gray-50"
          :aria-label="`Ir para página anterior (página ${currentPage - 1})`"
          @click.prevent="goToPage(currentPage - 1)"
        >
          <IconImage
            :src="Arrow"
            class="h-5 w-5 mr-2"
            alt="Anterior"
          />
          Anterior
        </button>

        <div class="flex gap-2 mx-8">
          <template
            v-for="page in visiblePages"
            :key="page"
          >
            <span
              v-if="page === '...'"
              class="flex items-center justify-center w-10 h-10 text-sm font-medium"
              :class="ellipsisTextClass"
            >
              {{ page }}
            </span>
            <button
              v-else
              :class="[
                'flex items-center justify-center w-10 h-10 border rounded-lg text-sm font-medium',
                page === currentPage ? pageNumberActiveClass : pageNumberInactiveClass,
              ]"
              :aria-label="page === currentPage ? `Página atual ${page}` : `Ir para página ${page}`"
              :aria-current="page === currentPage ? 'page' : undefined"
              @click.prevent="goToPage(Number(page))"
            >
              {{ page }}
            </button>
          </template>
        </div>

        <button
          :disabled="currentPage === totalPages || !totalPages"
          :class="{
            'opacity-50 cursor-not-allowed': currentPage === totalPages || !totalPages,
          }"
          class="flex items-center px-4 py-2 border border-gray-02 rounded-lg bg-white-01 text-sm font-medium text-gray-07 hover:bg-gray-50"
          :aria-label="`Ir para próxima página (página ${currentPage + 1})`"
          @click.prevent="goToPage(currentPage + 1)"
        >
          Próxima
          <IconImage
            :src="Arrow"
            class="h-5 w-5 rotate-180 ml-2"
            alt="Próxima"
          />
        </button>
      </nav>
    </div>
  </div>
</template>
